import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Announcer, ConnectDomain, EmailDomainStatus, EntitlementNotice } from "@/components/email/parts";
import { EmptyState, ErrorState, Spinner } from "@/components/ui";
import { useEligibleDomains, useEmailOverview } from "@/lib/email";

/**
 * Professional email for the whole business: every domain connected to it, with its DNS status, and a way to connect
 * more. It stands on its own permission (`email:read` / `email:manage`), so an Email Manager, who cannot open the
 * Domains pages, can do everything here. The server answers each part on that permission; this page only presents it.
 */
export function EmailPage() {
  const { canAny } = useOrganization();
  const overview = useEmailOverview();
  const [announcement, setAnnouncement] = useState("");
  // Whoever may manage e-mail somewhere (business-wide or on a domain folder) is offered the domains they can connect.
  const canManage = canAny("email:manage");
  const eligible = useEligibleDomains(canManage);

  if (overview.isPending) return <Spinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const { domains, providers, entitlement } = overview.data;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-[26px] font-bold text-fg">Email</h1>
        <p className="mt-1 text-sm text-fg-subtle">
          Whether the DNS records professional email needs (MX, SPF, DKIM and DMARC) are right for each
          domain.
        </p>
        <p className="mt-2 text-sm">
          <Link to="/email/mailboxes" className="font-semibold text-brand-700 underline">
            Manage mailboxes, aliases and forwarding
          </Link>
        </p>
      </div>
      <EntitlementNotice entitlement={entitlement} />
      {domains.length === 0 && !canManage && (
        <EmptyState
          title="No domain is connected to professional email"
          description="Someone with the Email Manager role can connect one."
        />
      )}
      {domains.map((row) => (
        <section key={row.id} aria-label={row.fqdn} className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-fg">{row.fqdn}</h2>
          <EmailDomainStatus row={row} onAnnounce={setAnnouncement} />
        </section>
      ))}
      {canManage && (
        <ConnectDomain
          choices={eligible.data?.domains ?? []}
          providers={providers}
          entitlement={entitlement}
          onAnnounce={setAnnouncement}
          subtitle="Connect another domain"
        />
      )}
      <Announcer message={announcement} />
    </div>
  );
}
